import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowRight, ArrowLeft, CheckCircle, Building2, Users, Phone, Mail, Calculator, TrendingDown } from 'lucide-react';
import './SavingsReviewModal.css';

const SavingsReviewModal = ({ isOpen, onClose }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    businessType: '',
    employeeCount: '',
    currentCarrier: '',
    hasUnlimitedData: '',
    companyName: '',
    workEmail: '',
    currentMonthlyCost: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);

  // Calculate savings based on real market data
  const calculateSavings = () => {
    const employees = parseInt(formData.employeeCount) || 0;
    
    const getCarrierPricing = () => {
      switch(formData.currentCarrier) {
        case 'verizon':
          return { retail: 85, optimized: 35, carrierName: 'Verizon' };
        case 'att':
          return { retail: 80, optimized: 35, carrierName: 'AT&T' };
        case 'tmobile':
          return { retail: 85, optimized: 35, carrierName: 'T-Mobile' };
        default:
          return { retail: 75, optimized: 35, carrierName: 'major carrier' };
      }
    };
    
    const pricing = getCarrierPricing();
    const monthlySavingsPerLine = pricing.retail - pricing.optimized;
    const monthlySavings = employees * monthlySavingsPerLine;
    const annualSavings = monthlySavings * 12;
    
    return {
      monthlySavings,
      annualSavings,
      monthlySavingsPerLine,
      percentageSaved: Math.round((monthlySavingsPerLine / pricing.retail) * 100),
      retailPrice: pricing.retail,
      optimizedPrice: pricing.optimized,
      carrierName: pricing.carrierName
    };
  };

  const savings = calculateSavings();

  // Handle form submission
  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    try {
      // TODO: Replace with your actual API endpoint
      const response = await fetch('/api/business-lead-capture/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          estimatedSavings: savings,
          source: 'business_savings_review',
          timestamp: new Date().toISOString()
        })
      });

      if (response.ok) {
        setIsComplete(true);
        // Track conversion event
        if (window.gtag) {
          window.gtag('event', 'business_lead_captured', {
            event_category: 'Lead Generation',
            event_label: 'Savings Review Complete',
            value: savings.annualSavings
          });
        }
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      // For now, still show success - you can add proper error handling later
      setIsComplete(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNext = () => {
    console.log('Next clicked, current step:', currentStep, 'form data:', formData);
    
    // Special handling for enterprise customers (100+ lines)
    if (currentStep === 2 && parseInt(formData.employeeCount) >= 150) {
      // For large enterprises, skip to contact info with enterprise messaging
      setCurrentStep(5);
      return;
    }
    
    if (currentStep < 5) {
      setCurrentStep(currentStep + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const isStepValid = () => {
    switch (currentStep) {
      case 1:
        return formData.businessType !== '';
      case 2:
        return formData.employeeCount !== '';
      case 3:
        return formData.currentCarrier !== '';
      case 4:
        return formData.hasUnlimitedData !== '';
      case 5:
        return formData.companyName !== '' && formData.workEmail !== '' && formData.workEmail.includes('@');
      default:
        return false;
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleModalClick = (e) => {
    e.stopPropagation();
  };

  // Handle body scroll and form reset
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      
      // Reset form
      setCurrentStep(1);
      setFormData({
        businessType: '',
        employeeCount: '',
        currentCarrier: '',
        hasUnlimitedData: '',
        companyName: '',
        workEmail: '',
        currentMonthlyCost: ''
      });
      setIsComplete(false);
      setIsSubmitting(false);
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const modalContent = (
    <div className="circledin-savings-modal-overlay" onClick={handleOverlayClick}>
      <motion.div 
        className="circledin-savings-modal-container"
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        onClick={handleModalClick}
      >
        {/* Header */}
        <div className="circledin-savings-modal-header">
          <div className="circledin-savings-modal-title">
            <Calculator size={24} className="circledin-savings-modal-title-icon" />
            <h2 className="circledin-savings-modal-title-text">Free Business Savings Review</h2>
          </div>
          <button onClick={onClose} className="circledin-savings-modal-close-button">
            <X size={24} />
          </button>
        </div>

        {/* Progress Bar */}
        {!isComplete && (
          <div className="circledin-savings-modal-progress-container">
            <div className="circledin-savings-modal-progress-bar">
              <div 
                className="circledin-savings-modal-progress-fill"
                style={{ width: `${(currentStep / 5) * 100}%` }}
              />
            </div>
            <span className="circledin-savings-modal-progress-text">{Math.round((currentStep / 5) * 100)}% complete</span>
          </div>
        )}

        {/* Content */}
        <div className="circledin-savings-modal-content">
          <AnimatePresence mode="wait">
            {!isComplete ? (
              <motion.div
                key={currentStep}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                {/* Step 1: Business Type */}
                {currentStep === 1 && (
                  <div className="circledin-savings-modal-step-content">
                    <div className="circledin-savings-modal-step-icon">
                      <Building2 size={32} />
                    </div>
                    <h3 className="circledin-savings-modal-step-title">Who are you reviewing this plan for?</h3>
                    <p className="circledin-savings-modal-step-description">This helps us personalize your savings analysis.</p>
                    
                    <div className="circledin-savings-modal-option-grid">
                      {[
                        { value: 'startup', label: 'My startup' },
                        { value: 'company', label: 'My company/team' },
                        { value: 'clients', label: 'My clients' },
                        { value: 'myself', label: 'Myself' }
                      ].map((option) => (
                        <button
                          key={option.value}
                          className={`circledin-savings-modal-option-button ${formData.businessType === option.value ? 'circledin-savings-modal-option-selected' : ''}`}
                          onClick={() => setFormData({...formData, businessType: option.value})}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Step 2: Employee Count */}
                {currentStep === 2 && (
                  <div className="circledin-savings-modal-step-content">
                    <div className="circledin-savings-modal-step-icon">
                      <Users size={32} />
                    </div>
                    <h3 className="circledin-savings-modal-step-title">How many phone lines {formData.businessType === 'startup' ? 'does your startup' : formData.businessType === 'myself' ? 'do you' : 'does your company'} manage?</h3>
                    <p className="circledin-savings-modal-step-description">This helps us calculate your potential savings.</p>
                    
                    <div className="circledin-savings-modal-option-grid">
                      {[
                        { value: '3', label: '1-5 lines' },
                        { value: '8', label: '6-10 lines' },
                        { value: '18', label: '11-25 lines' },
                        { value: '35', label: '26-50 lines' },
                        { value: '75', label: '51-100 lines' },
                        { value: '150', label: '100+ lines' }
                      ].map((option) => (
                        <button
                          key={option.value}
                          className={`circledin-savings-modal-option-button ${formData.employeeCount === option.value ? 'circledin-savings-modal-option-selected' : ''}`}
                          onClick={() => setFormData({...formData, employeeCount: option.value})}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Step 3: Current Carrier */}
                {currentStep === 3 && (
                  <div className="circledin-savings-modal-step-content">
                    <div className="circledin-savings-modal-step-icon">
                      <Phone size={32} />
                    </div>
                    <h3 className="circledin-savings-modal-step-title">Which carrier are you currently using?</h3>
                    <p className="circledin-savings-modal-step-description">We work with all major carriers to unlock group savings.</p>
                    
                    {formData.currentCarrier && (
                      <div className="circledin-savings-modal-carrier-insight">
                        <span className="circledin-savings-modal-insight-text">
                          💡 {formData.currentCarrier === 'verizon' 
                            ? 'Most Verizon business customers pay $85/line — we reduce that by up to 60%.'
                            : formData.currentCarrier === 'att' 
                            ? 'AT&T business plans average $75–$85 — we help you unlock better features at half the cost.'
                            : formData.currentCarrier === 'tmobile'
                            ? 'T-Mobile Business Unlimited is often $85/line — let\'s check if you\'re overpaying.'
                            : 'Most teams are overpaying by 40–60%. Let\'s review your plan and find out.'
                          }
                        </span>
                      </div>
                    )}
                    
                    <div className="circledin-savings-modal-option-grid">
                      {[
                        { value: 'verizon', label: 'Verizon' },
                        { value: 'att', label: 'AT&T' },
                        { value: 'tmobile', label: 'T-Mobile' },
                        { value: 'other', label: 'Other/Not Sure' }
                      ].map((option) => (
                        <button
                          key={option.value}
                          className={`circledin-savings-modal-option-button ${formData.currentCarrier === option.value ? 'circledin-savings-modal-option-selected' : ''}`}
                          onClick={() => setFormData({...formData, currentCarrier: option.value})}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Step 4: Current Plan Features */}
                {currentStep === 4 && (
                  <div className="circledin-savings-modal-step-content">
                    <div className="circledin-savings-modal-step-icon">
                      <TrendingDown size={32} />
                    </div>
                    <h3 className="circledin-savings-modal-step-title">Are you getting everything you're paying for?</h3>
                    <p className="circledin-savings-modal-step-description">Many businesses pay top dollar but still don't get unlimited hotspot, full-speed data, or perks. We help you unlock those — without changing carriers.</p>
                    
                    <div className="circledin-savings-modal-option-grid">
                      {[
                        { value: 'yes', label: 'Yes, unlimited everything' },
                        { value: 'limited', label: 'Limited hotspot/data' },
                        { value: 'no', label: 'No unlimited features' },
                        { value: 'unsure', label: 'Not sure' }
                      ].map((option) => (
                        <button
                          key={option.value}
                          className={`circledin-savings-modal-option-button ${formData.hasUnlimitedData === option.value ? 'circledin-savings-modal-option-selected' : ''}`}
                          onClick={() => setFormData({...formData, hasUnlimitedData: option.value})}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Step 5: Contact Information */}
                {currentStep === 5 && (
                  <div className="circledin-savings-modal-step-content">
                    <div className="circledin-savings-modal-step-icon">
                      <Mail size={32} />
                    </div>
                    <h3 className="circledin-savings-modal-step-title">
                      {parseInt(formData.employeeCount) >= 150 
                        ? `Your enterprise could save ${savings.annualSavings.toLocaleString()}+/year. Let's discuss custom solutions.`
                        : `You're losing ${savings.annualSavings.toLocaleString()}/year. Let's fix that.`
                      }
                    </h3>
                    <p className="circledin-savings-modal-step-description">
                      {parseInt(formData.employeeCount) >= 150
                        ? `With ${formData.employeeCount}+ lines, you need enterprise-grade optimization. We'll send you a detailed analysis and connect you with our enterprise team for custom pricing and implementation.`
                        : `We'll send you a detailed plan to unlock these savings — and show how other ${formData.businessType === 'startup' ? 'startups' : 'companies'} did the same.`
                      }
                    </p>
                    
                    {/* Urgency Badge */}
                    <div className="circledin-savings-modal-urgency-badge">
                      {parseInt(formData.employeeCount) >= 150
                        ? '🎯 Enterprise priority review - 24 hour response'
                        : '⏰ Your savings are reserved for 5 days'
                      }
                    </div>
                    
                    {/* Savings Preview */}
                    <div className="circledin-savings-modal-savings-preview">
                      <div className="circledin-savings-modal-savings-highlight">
                        <motion.div 
                          className="circledin-savings-modal-savings-amount"
                          animate={{ scale: [1, 1.02, 1] }}
                          transition={{ duration: 2, repeat: Infinity }}
                        >
                          ${savings.annualSavings.toLocaleString()}
                        </motion.div>
                        <div className="circledin-savings-modal-savings-label">Potential Annual Savings</div>
                        <div className="circledin-savings-modal-calculation-breakdown">
                          Based on {formData.employeeCount} lines @ ${savings.monthlySavingsPerLine}/mo savings each
                        </div>
                      </div>
                      <div className="circledin-savings-modal-savings-details">
                        <div className="circledin-savings-modal-detail-item">
                          <span>Monthly Savings:</span>
                          <span>${savings.monthlySavings.toLocaleString()}</span>
                        </div>
                        <div className="circledin-savings-modal-detail-item">
                          <span>Cost Reduction:</span>
                          <span>{savings.percentageSaved}%</span>
                        </div>
                      </div>
                      
                      <div className="circledin-savings-modal-pricing-breakdown">
                        <div className="circledin-savings-modal-pricing-item">
                          <span>Current {savings.carrierName} cost:</span>
                          <span>${savings.retailPrice}/line/mo</span>
                        </div>
                        <div className="circledin-savings-modal-pricing-item optimized">
                          <span>Optimized cost:</span>
                          <span>${savings.optimizedPrice}/line/mo</span>
                        </div>
                      </div>
                      
                      <div className="circledin-savings-modal-disclaimer">
                        *Savings based on published {savings.carrierName} business plan pricing and real Circledin customer outcomes. Actual savings may vary.
                      </div>
                    </div>

                    <div className="circledin-savings-modal-input-group">
                      <input
                        type="text"
                        placeholder="Company Name"
                        value={formData.companyName}
                        onChange={(e) => setFormData({...formData, companyName: e.target.value})}
                        className="circledin-savings-modal-form-input"
                      />
                      <input
                        type="email"
                        placeholder="Work Email"
                        value={formData.workEmail}
                        onChange={(e) => setFormData({...formData, workEmail: e.target.value})}
                        className="circledin-savings-modal-form-input"
                      />
                    </div>
                  </div>
                )}
              </motion.div>
            ) : (
              // Success State
              <motion.div
                className="circledin-savings-modal-success-content"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
              >
                <div className="circledin-savings-modal-success-icon">
                  <CheckCircle size={48} />
                </div>
                <h3 className="circledin-savings-modal-success-title">Your savings report is on the way!</h3>
                <p className="circledin-savings-modal-success-description">We've sent your personalized analysis to <strong>{formData.workEmail}</strong></p>
                
                <div className="circledin-savings-modal-success-savings">
                  <div className="circledin-savings-modal-success-card">
                    <div className="circledin-savings-modal-success-number">${savings.annualSavings.toLocaleString()}</div>
                    <div className="circledin-savings-modal-success-text">estimated annual savings</div>
                  </div>
                </div>

                <div className="circledin-savings-modal-next-steps">
                  <h4>What happens next?</h4>
                  <ul>
                    <li>📧 Check your email for your detailed savings breakdown</li>
                    <li>📞 A cost-savings expert will follow up within 1 business day</li>
                    <li>💰 Most teams start saving within 2 weeks</li>
                  </ul>
                </div>

                <button onClick={onClose} className="circledin-savings-modal-success-button">
                  Got it, thanks!
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Footer Navigation */}
        {!isComplete && (
          <div className="circledin-savings-modal-footer">
            <button 
              onClick={handleBack}
              className="circledin-savings-modal-nav-button circledin-savings-modal-nav-secondary"
              disabled={currentStep === 1}
            >
              <ArrowLeft size={18} />
              Back
            </button>
            
            <button 
              onClick={handleNext}
              className="circledin-savings-modal-nav-button circledin-savings-modal-nav-primary"
              disabled={!isStepValid() || isSubmitting}
            >
              {isSubmitting ? (
                'Submitting...'
              ) : currentStep === 5 ? (
                'Send My Report'
              ) : (
                <>
                  Next
                  <ArrowRight size={18} />
                </>
              )}
            </button>
          </div>
        )}
      </motion.div>
    </div>
  );

  // Use React Portal to render modal outside of normal DOM hierarchy
  return createPortal(modalContent, document.body);
};

export default SavingsReviewModal;